import { useState, useEffect } from "react";
import { ArrowLeft, AlertTriangle, Wind, Gauge, Droplets, Calendar, Tag, User, FileText } from "lucide-react";
import { fetchStormDetail, fetchStormWarnings } from "@/lib/stormsQueries";

const WARNING_COLOURS = {
  yellow: "#FFD700",
  amber: "#FF8C00",
  red: "#D32F2F",
};

const WARNING_BG_COLOURS = {
  yellow: "rgba(255, 215, 0, 0.08)",
  amber: "rgba(255, 140, 0, 0.08)",
  red: "rgba(211, 47, 47, 0.08)",
};

function WarningPill({ warning }) {
  const colour = WARNING_COLOURS[warning.level] || "#FFD700";
  const bg = WARNING_BG_COLOURS[warning.level] || "rgba(255, 215, 0, 0.08)";
  const level = warning.level ? warning.level.toUpperCase() : "";
  const hazard = warning.hazard_type
    ? warning.hazard_type.charAt(0).toUpperCase() + warning.hazard_type.slice(1).toLowerCase()
    : "";
  return (
    <div
      className="flex items-start gap-2 px-3 py-2"
      style={{ border: `2px solid ${colour}`, backgroundColor: bg }}
    >
      <AlertTriangle style={{ color: colour }} className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm text-[#F9FAFB] font-semibold tracking-wide">
          {level} {hazard}
        </p>
        <p className="text-xs text-[#6B7280]">Issued by {warning.issuing_body}</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, unit, color }) {
  return (
    <div className="border border-white/10 bg-[#111827] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="text-xs uppercase tracking-wider text-[#6B7280]">{label}</p>
      </div>
      {value != null ? (
        <p className={`text-2xl font-bold ${color}`}>
          {value} <span className="text-sm font-normal text-[#6B7280]">{unit}</span>
        </p>
      ) : (
        <p className="text-[#6B7280] text-sm">No data</p>
      )}
    </div>
  );
}

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function StormDetailPage({ name, season, onBack, onBackToSeason }) {
  const [storm, setStorm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [warningsLoading, setWarningsLoading] = useState(false);

  useEffect(() => {
    if (!name || !season) return;
    setLoading(true);
    fetchStormDetail(name, season)
      .then((s) => {
        setStorm(s);
        if (s?.id) {
          setWarningsLoading(true);
          fetchStormWarnings(s.id)
            .then(setWarnings)
            .catch(() => {})
            .finally(() => setWarningsLoading(false));
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [name, season]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 w-48 bg-[#111827] border border-white/10" />
        <div className="h-32 bg-[#111827] border border-white/10" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-[#111827] border border-white/10" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-sm">Failed to load storm: {error}</p>
      </div>
    );
  }

  if (!storm) {
    return (
      <div className="text-[#6B7280] text-sm text-center py-16">Storm not found.</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <div className="flex flex-wrap gap-3 text-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#6B7280] hover:text-cyan-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All Seasons
        </button>
        {onBackToSeason && (
          <>
            <span className="text-[#6B7280]">/</span>
            <button
              onClick={() => onBackToSeason(season)}
              className="text-[#6B7280] hover:text-cyan-400 transition-colors"
            >
              {season} Season
            </button>
          </>
        )}
      </div>

      {/* Storm header */}
      <div className="border border-white/10 bg-[#111827] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-cyan-500/10 text-cyan-400 text-sm font-bold border border-cyan-500/20">
                {storm.naming_letter}
              </span>
              <h2 className="text-3xl font-bold text-[#F9FAFB]">Storm {storm.name}</h2>
            </div>
            <p className="text-[#6B7280] text-sm">{storm.season} Season</p>
          </div>
          {storm.named_by && (
            <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
              <User className="w-4 h-4" />
              Named by <span className="text-[#F9FAFB] ml-1">{storm.named_by}</span>
            </div>
          )}
        </div>

        {/* Date range */}
        <div className="mt-4 flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2 text-[#9CA3AF]">
            <Calendar className="w-4 h-4 text-cyan-500/60" />
            <span>Start: <span className="text-[#F9FAFB]">{fmt(storm.start_date)}</span></span>
          </div>
          {storm.end_date && (
            <div className="flex items-center gap-2 text-[#9CA3AF]">
              <Calendar className="w-4 h-4 text-cyan-500/60" />
              <span>End: <span className="text-[#F9FAFB]">{fmt(storm.end_date)}</span></span>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Wind} label="Peak Gust" value={storm.peak_gust != null ? storm.peak_gust : storm.peak_gust_mph} unit="mph" color="text-cyan-400" />
        <StatCard icon={Gauge} label="Peak Pressure" value={storm.peak_pressure != null ? storm.peak_pressure : storm.peak_pressure_hpa} unit="hPa" color="text-blue-400" />
        <StatCard icon={Droplets} label="Rainfall" value={storm.peak_rainfall != null ? storm.peak_rainfall : storm.rainfall_mm} unit="mm" color="text-sky-400" />
      </div>

      {/* Warnings */}
      {warningsLoading ? (
        <div className="border border-white/10 bg-[#111827] p-4">
          <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="border border-white/10 bg-[#111827] p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#6B7280]" />
            <h3 className="text-sm font-semibold text-[#F9FAFB]">Warnings</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {["Wind", "Rain", "Snow"].map((type) => {
              const lowerType = type.toLowerCase();
              const filtered = warnings.filter(
                (w) => w.hazard_type && w.hazard_type.toLowerCase() === lowerType
              );
              return (
                <div key={type}>
                  <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">{type} Warnings</p>
                  {filtered.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {filtered.map((w) => (
                        <WarningPill key={w.id} warning={w} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#6B7280]">No {type} warnings were active for this storm.</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {storm.notes && (
        <div className="border border-white/10 bg-[#111827] p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-[#6B7280]" />
            <h3 className="text-sm font-semibold text-[#F9FAFB]">Notes</h3>
          </div>
          <p className="text-sm text-[#9CA3AF] leading-relaxed">{storm.notes}</p>
        </div>
      )}
    </div>
  );
}
